// The learner's year, as one figure in two panels.
//
// STOCK on top: everything touched so far, mastered above the line and still
// open below it. FLOW underneath: what was newly met in that same bucket,
// split by where it sat relative to the learner's grade.
//
// One figure and not two charts, for three reasons that all point the same way.
// They answer one question together — a flat stretch on top means either
// "nothing new arrived" or "plenty arrived and none of it stuck", and only the
// panel underneath says which. They must be read at the same moment to do that,
// so they have to fit on a screen together; the working target is a Chromebook,
// which is what the heights here are sized against. And two separate charts had
// two x scales (points at i/(n-1) above, band centres below), so a column in one
// did not sit over the same column in the other — a chart of the same months
// that quietly did not line up.
//
// Both panels now hang off one `x`, one crosshair and one tooltip. The stock
// series sits on band CENTRES rather than on the panel edges, which costs half a
// band of margin at each end and buys a bar that is exactly under the point it
// belongs to.
//
// Everything about how the marks are painted — a standard enters in the band it
// was reached in, texture carries grade, the two directions share one scale — is
// documented on `palette.js` and in the caption the container puts underneath.
import React, { useMemo, useState } from "react";
import { MUTED, GRID, INK } from "../../impact/tokens";
import { useElementWidth } from "../../impact/ui";
import { STATES, stateOf, REL, REL_ORDER, GrowthDefs, fillFor } from "./palette";
import { bucketLabel, countStep, labelEvery } from "./scale";
import { Tip, TipNote } from "./tip";

const SCOPE = "gtl";

// Sized against a Chromebook: 1366x768, of which the app header, this page's
// title, the nav, the scope row and the control bar take about 420px, leaving
// ~350 for a figure plus its legend and caption. 278px total — a stock panel
// tall enough to hold a diverging area with several gridline steps, a flow
// panel tall enough for a four-way stack to be countable, and no more.
//
// Two things above this figure still push it below the fold on that screen and
// both are conditional: the security-question banner (until the account sets
// one) and the reconstructed-year note (past school years only).
const PAD = { l: 46, r: 150, t: 14, b: 22 };
const STOCK_H = 168;
const GAP = 14;
const FLOW_H = 60;
const H = PAD.t + STOCK_H + GAP + FLOW_H + PAD.b;
const STOCK_T = PAD.t;
const FLOW_T = PAD.t + STOCK_H + GAP;
const MIN_W = 460;
const MAX_BAR = 44;

const gradeName = (g) => (g === 0 ? "K" : String(g));

const GrowthTimeline = ({ data, shade }) => {
  const [ref, measured] = useElementWidth();
  const [hover, setHover] = useState(null);
  const width = Math.max(MIN_W, measured || MIN_W);

  const geom = useMemo(() => {
    const buckets = data.buckets || [];
    const n = buckets.length;
    const series = data.series || [];
    const cum = series.map((s) => s.cumulative_relative);
    const iw = width - PAD.l - PAD.r;
    const band = iw / Math.max(1, n);
    const x = (i) => PAD.l + band * (i + 0.5);

    // ---- stock panel ------------------------------------------------------
    const total = (i, states) =>
      states.reduce(
        (t, s) => t + REL_ORDER.reduce((u, r) => u + (cum[i] ? cum[i][s][r] : 0), 0),
        0
      );
    const up = buckets.map((_, i) => total(i, ["outright", "assisted"]));
    const dn = buckets.map((_, i) => total(i, ["not_yet"]));
    const maxUp = Math.max(1, ...up);
    const maxDn = Math.max(1, ...dn);
    const span = maxUp + maxDn;
    const y0 = STOCK_T + STOCK_H * (maxUp / span);
    const ys = (v) => y0 - (v / span) * STOCK_H;

    const offUp = buckets.map(() => 0);
    const offDn = buckets.map(() => 0);
    const bands = [];
    const push = (state, rel, dir) => {
      const off = dir > 0 ? offUp : offDn;
      const lo = [];
      const hi = [];
      let any = false;
      for (let i = 0; i < n; i += 1) {
        const v = cum[i] ? cum[i][state][rel] : 0;
        if (v) any = true;
        lo.push(off[i]);
        off[i] += v;
        hi.push(off[i]);
      }
      if (!any) return;
      let d = "";
      for (let i = 0; i < n; i += 1)
        d += `${i ? "L" : "M"}${x(i)},${ys(dir * hi[i])} `;
      for (let i = n - 1; i >= 0; i -= 1) d += `L${x(i)},${ys(dir * lo[i])} `;
      bands.push({ key: `${state}-${rel}-${dir}`, d: `${d}Z`, state, rel });
    };
    STATES.filter((s) => s !== "not_yet").forEach((s) =>
      REL_ORDER.forEach((r) => push(s, r, 1))
    );
    REL_ORDER.forEach((r) => push("not_yet", r, -1));

    const stockStep = countStep(span);
    const stockRules = [];
    for (let v = stockStep; v <= maxUp; v += stockStep) stockRules.push(v);
    for (let v = stockStep; v <= maxDn; v += stockStep) stockRules.push(-v);

    const last = cum[n - 1];
    const endTotal = (state) =>
      last ? REL_ORDER.reduce((t, r) => t + last[state][r], 0) : 0;

    // ---- flow panel -------------------------------------------------------
    const entered = series.map((s) =>
      REL_ORDER.reduce((t, r) => t + s.relative[r], 0)
    );
    const maxNew = Math.max(1, ...entered);
    const yf = (v) => FLOW_T + FLOW_H - (v / maxNew) * FLOW_H;
    const bw = Math.max(6, Math.min(MAX_BAR, band - 8));
    const bars = [];
    buckets.forEach((b, i) => {
      let acc = 0;
      REL_ORDER.forEach((r) => {
        const v = series[i] ? series[i].relative[r] : 0;
        if (!v) return;
        const y1 = yf(acc);
        acc += v;
        const y2 = yf(acc);
        bars.push({
          key: `${i}-${r}`,
          i,
          rel: r,
          v,
          x: x(i) - bw / 2,
          y: y2,
          // Floor at 3px so a single standard is still a visible mark; 2px comes
          // off for the surface gap that separates one segment from the next.
          h: Math.max(3, y1 - y2 - 2),
        });
      });
    });
    // One rule, at the top of the tallest bar. A 66px panel has room for a
    // number, not for a scale.
    const flowRule = maxNew;

    return { buckets, n, iw, band, x, ys, y0, bands, stockRules, cum, endTotal,
             bars, yf, flowRule, entered, bw, every: labelEvery(n, iw) };
  }, [data, width]);

  if (!geom.n) return null;

  const nearest = (evt) => {
    const box = evt.currentTarget.getBoundingClientRect();
    const px = evt.clientX - box.left;
    let best = 0;
    let bestD = Infinity;
    geom.buckets.forEach((_, i) => {
      const d = Math.abs(geom.x(i) - px);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    return best;
  };

  const hoveredCum = hover === null ? null : geom.cum[hover];
  const o = geom.endTotal("outright");
  const a = geom.endTotal("assisted");
  const ny = geom.endTotal("not_yet");

  // End-of-window totals, written beside the band they label. Amber sits below
  // 3:1 against white, so "not yet" is never carried by its fill alone — here,
  // in the legend, and in the table view.
  const endLabel = (v, y, state, text) =>
    v ? (
      <g key={state}>
        <rect
          x={PAD.l + geom.iw + 10}
          y={y - 6}
          width="9"
          height="9"
          rx="2.5"
          fill={stateOf(state).fill}
        />
        <text x={PAD.l + geom.iw + 25} y={y + 3} fontSize="11.5" fill={INK}>
          {v} {text}
        </text>
      </g>
    ) : null;

  const per = data.interval === "week" ? "week" : "month";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <svg
        width={width}
        height={H}
        style={{ display: "block", touchAction: "none" }}
        role="img"
        aria-label={
          "A learner's year: standards accumulated by mastery state, over the " +
          "grade-relative placement of what was newly attempted in each bucket"
        }
        onMouseMove={(e) => setHover(nearest(e))}
        onMouseLeave={() => setHover(null)}
      >
        <GrowthDefs scope={SCOPE} />

        {/* ---- stock panel ------------------------------------------- */}
        {geom.stockRules.map((v) => (
          <g key={`s${v}`}>
            <line
              x1={PAD.l}
              x2={PAD.l + geom.iw}
              y1={geom.ys(v)}
              y2={geom.ys(v)}
              stroke={GRID}
              strokeWidth="1"
            />
            <text
              x={PAD.l - 8}
              y={geom.ys(v) + 3.5}
              textAnchor="end"
              fontSize="10"
              fill={MUTED}
            >
              {Math.abs(v)}
            </text>
          </g>
        ))}

        {geom.bands.map((b) => (
          <path
            key={b.key}
            d={b.d}
            fill={fillFor(SCOPE, b.state, b.rel, shade)}
            // A 2px surface gap between adjacent fills: without it two textures
            // of the same hue meet with no edge and read as one band.
            stroke="#fff"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        ))}

        <line
          x1={PAD.l}
          x2={PAD.l + geom.iw}
          y1={geom.y0}
          y2={geom.y0}
          stroke="#c3c2b7"
          strokeWidth="1.5"
        />

        {/* Panel titles run vertically in the left margin. Laid flat they would
            collide with the gridline values, and this figure has no vertical
            room to spend on a heading per panel. */}
        <text
          transform={`translate(11 ${(STOCK_T + geom.y0) / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize="10"
          fill={MUTED}
        >
          mastered ▲
        </text>
        <text
          transform={`translate(11 ${(geom.y0 + STOCK_T + STOCK_H) / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize="10"
          fill={MUTED}
        >
          still open ▼
        </text>

        {endLabel(o, geom.ys(o / 2), "outright", "outright")}
        {endLabel(a, geom.ys(o + a / 2), "assisted", "with ABii's help")}
        {endLabel(ny, geom.ys(-ny / 2), "not_yet", "not yet")}

        {/* ---- flow panel -------------------------------------------- */}
        <line
          x1={PAD.l}
          x2={PAD.l + geom.iw}
          y1={geom.yf(geom.flowRule)}
          y2={geom.yf(geom.flowRule)}
          stroke={GRID}
          strokeWidth="1"
        />
        <text
          x={PAD.l - 8}
          y={geom.yf(geom.flowRule) + 3.5}
          textAnchor="end"
          fontSize="10"
          fill={MUTED}
        >
          {geom.flowRule}
        </text>

        {geom.bars.map((b) => (
          <rect
            key={b.key}
            x={b.x}
            y={b.y}
            width={geom.bw}
            height={b.h}
            rx="3"
            fill={fillFor(SCOPE, "neutral", b.rel, shade)}
            stroke="#fff"
            strokeWidth="1"
            opacity={hover === null || hover === b.i ? 1 : 0.55}
          />
        ))}

        <line
          x1={PAD.l}
          x2={PAD.l + geom.iw}
          y1={FLOW_T + FLOW_H}
          y2={FLOW_T + FLOW_H}
          stroke="#c3c2b7"
          strokeWidth="1"
        />
        <text
          transform={`translate(11 ${FLOW_T + FLOW_H / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize="10"
          fill={MUTED}
        >
          new
        </text>
        <text
          x={PAD.l + geom.iw + 10}
          y={FLOW_T + FLOW_H / 2 + 3}
          fontSize="11.5"
          fill={MUTED}
        >
          {/* Whose year this is. On a cohort the grade channel is per learner
              -- the class spans several -- so naming one here would be wrong;
              the count is what the reader needs instead. */}
          {data.cohort
            ? `${data.cohort.learners} learner${
                data.cohort.learners === 1 ? "" : "s"
              }`
            : data.learner_grade === null || data.learner_grade === undefined
            ? "grade unknown"
            : `learner in grade ${gradeName(data.learner_grade)}`}
        </text>

        {/* ---- shared x axis and crosshair ---------------------------- */}
        {geom.buckets.map((b, i) =>
          i % geom.every === 0 ? (
            <text
              key={b.start}
              x={geom.x(i)}
              y={H - 8}
              textAnchor="middle"
              fontSize="10.5"
              fill={hover === i ? INK : MUTED}
            >
              {bucketLabel(b, data.interval)}
            </text>
          ) : null
        )}

        {/* The crosshair runs through BOTH panels. It is the cue that says
            these are one figure and that the bar under the pointer belongs to
            the column above it. */}
        {hover !== null && (
          <line
            x1={geom.x(hover)}
            x2={geom.x(hover)}
            y1={STOCK_T}
            y2={FLOW_T + FLOW_H}
            stroke="#c3c2b7"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        )}
      </svg>

      {hoveredCum && (
        <Tip x={geom.x(hover)} y={6} width={width}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            {bucketLabel(geom.buckets[hover], data.interval)}
          </div>
          {STATES.map((s) => {
            const t = REL_ORDER.reduce((u, r) => u + hoveredCum[s][r], 0);
            if (!t) return null;
            const parts = REL_ORDER.filter((r) => hoveredCum[s][r]).map(
              (r) => `${hoveredCum[s][r]} ${REL[r].short}`
            );
            return (
              <div key={s}>
                <strong>{t}</strong> {stateOf(s).label}
                <br />
                <TipNote>&nbsp;&nbsp;{parts.join(" · ")}</TipNote>
              </div>
            );
          })}
          <div style={{ marginTop: 4 }}>
            <strong>{geom.entered[hover]}</strong> new this {per}
            {geom.entered[hover] > 0 && (
              <>
                <br />
                <TipNote>
                  &nbsp;&nbsp;
                  {REL_ORDER.filter((r) => data.series[hover].relative[r])
                    .map((r) => `${data.series[hover].relative[r]} ${REL[r].short}`)
                    .join(" · ")}
                </TipNote>
              </>
            )}
          </div>
          <TipNote>
            {data.series[hover].converted} converted this {per}
          </TipNote>
        </Tip>
      )}
    </div>
  );
};

export { GrowthTimeline };
